import { getRelatedDevice } from 'features/user-media/utils/getRelatedDevice';
import {
  EnumeratedMediaDevices,
  type PendingMediaDeviceInfo,
  UnwrappedMediaDeviceInfo,
} from 'features/user-media/types';
import { safeLocalStorage } from 'utils/storage';
import { findDeviceByLabelOrId } from 'features/user-media/utils/index';

// Resolve persisted device. Try to get the relative device if applicable.
export const resolvePersistedDevice = (
  mediaDevices: EnumeratedMediaDevices,
  kind: MediaDeviceKind
): string | undefined => {
  const persistedDeviceString = safeLocalStorage.getItem(kind);
  if (!persistedDeviceString) {
    return undefined;
  }

  try {
    const parsedDevice = JSON.parse(persistedDeviceString);

    // Handle pending devices (from SDK before permissions granted)
    if ('isPending' in parsedDevice && parsedDevice.isPending) {
      const pendingDevice = parsedDevice as PendingMediaDeviceInfo;
      const resolvedDevice = findDeviceByLabelOrId(mediaDevices[kind], pendingDevice.label);

      if (resolvedDevice) {
        safeLocalStorage.setItem(kind, JSON.stringify(resolvedDevice));
        return getRelatedDevice(mediaDevices[kind], resolvedDevice).deviceId;
      }

      return undefined;
    }

    // Handle resolved devices (normal JSON format)
    const deviceObject = parsedDevice as UnwrappedMediaDeviceInfo;
    if (deviceObject.label && deviceObject.kind) {
      const device = mediaDevices[kind].find((d) => d.label === deviceObject.label);
      if (device) {
        return getRelatedDevice(mediaDevices[kind], device).deviceId;
      }
    }
  } catch {
    // Handle legacy string format (backward compatibility)
    const availableDevices = mediaDevices[kind];
    const device = findDeviceByLabelOrId(availableDevices, persistedDeviceString);

    if (device) {
      // Migrate legacy string to new JSON format
      safeLocalStorage.setItem(kind, JSON.stringify(device));

      return getRelatedDevice(mediaDevices[kind], device).deviceId;
    }
  }

  return undefined;
};
